import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type TransitionPhase = 'idle' | 'exit' | 'enter';

interface PageTransitionState {
  phase: TransitionPhase;
  displayKey: string;
}

/**
 * Hook que gere transições suaves entre páginas.
 * - Fase "exit": página atual faz fade-out (50ms)
 * - Fase "enter": nova página faz fade-in + slide-up (200ms)
 * - Fase "idle": animação concluída, estado estável
 */
export function usePageTransition(durationMs = 180) {
  const location = useLocation();
  const [state, setState] = useState<PageTransitionState>({
    phase: 'idle',
    displayKey: location.pathname,
  });
  const prevPathRef = useRef(location.pathname);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const newPath = location.pathname;
    if (newPath === prevPathRef.current) return;

    // Limpar timer anterior se existir
    if (timerRef.current) clearTimeout(timerRef.current);

    // Fase 1: exit (fade-out rápido)
    setState({ phase: 'exit', displayKey: prevPathRef.current });

    timerRef.current = setTimeout(() => {
      // Fase 2: trocar conteúdo + enter (fade-in + slide)
      prevPathRef.current = newPath;
      setState({ phase: 'enter', displayKey: newPath });

      timerRef.current = setTimeout(() => {
        // Fase 3: idle (animação concluída)
        setState({ phase: 'idle', displayKey: newPath });
      }, durationMs);
    }, 60); // exit duration: 60ms

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [location.pathname, durationMs]);

  const style: React.CSSProperties = {
    transition: `opacity ${durationMs}ms ease, transform ${durationMs}ms ease`,
    opacity: state.phase === 'exit' ? 0 : 1,
    transform: state.phase === 'enter'
      ? 'translateY(6px)'
      : state.phase === 'exit'
        ? 'translateY(-4px)'
        : 'translateY(0)',
    willChange: state.phase !== 'idle' ? 'opacity, transform' : 'auto',
  };

  return {
    phase: state.phase,
    displayKey: state.displayKey,
    style,
    isAnimating: state.phase !== 'idle',
  };
}